import { toNumber } from "@/lib/calculators/truck-loading-merged/types";
import type { FitResult, TruckSpec } from "@/lib/calculators/truck-loading-merged/types";
import { PRODUCT_CONFIGS } from "@/lib/calculators/truck-loading-merged/product-presets";
import type { AdditionalItem } from "./use-calculator-state";

export function clampOnCommit(raw: string, max: number): number {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.min(Math.round(parsed), max);
}

export function getAdditionalItemLabel(addedItem: AdditionalItem): string {
  if (addedItem.presetId && addedItem.presetId !== "custom") {
    for (const productConfig of Object.values(PRODUCT_CONFIGS)) {
      for (const preset of productConfig.presets) {
        if (
          `${productConfig.slug}__${preset.id}` === addedItem.presetId &&
          preset.id !== "custom"
        ) {
          return `${preset.label} ${productConfig.singularLabel}`;
        }
      }
    }
  }
  const itemLength = Math.round(toNumber(addedItem.length));
  const itemWidth = Math.round(toNumber(addedItem.width));
  const itemHeight = Math.round(toNumber(addedItem.height));
  if (itemLength > 0 && itemWidth > 0 && itemHeight > 0) {
    return `${itemLength}×${itemWidth}×${itemHeight}`;
  }
  if (itemLength > 0 && itemWidth > 0) {
    return `${itemLength}×${itemWidth}`;
  }
  return "item";
}

export interface ResultsProps {
  truck: TruckSpec;
  result: FitResult;
  hasWeight: boolean;
  perFloor: number;
  stackLayers: number;
  singularLabel: string;
  pluralLabel: string;
  specificQtyEnabled: boolean;
  targetQty: string;
  additionalItems: Array<AdditionalItem>;
  onPerFloorChange: (value: number) => void;
  onStackLayersChange: (value: number) => void;
}

export interface ComputedTotals {
  effectiveTotal: number;
  grandTotal: number;
  trucksNeeded: number | null;
  isWeightLimited: boolean;
  hasAdditionalItems: boolean;
  activeAdditionalItems: Array<AdditionalItem & { parsedQuantity: number }>;
  parsedTargetQty: number;
}

function activeItems(additionalItems: Array<AdditionalItem>) {
  return additionalItems
    .map((addedItem) => ({
      ...addedItem,
      parsedQuantity: Math.max(Math.round(toNumber(addedItem.quantity)), 0),
    }))
    .filter(
      (addedItem) =>
        addedItem.parsedQuantity > 0 &&
        toNumber(addedItem.length) > 0 &&
        toNumber(addedItem.width) > 0,
    );
}

export function computeResultTotals(props: ResultsProps): ComputedTotals {
  const {
    perFloor,
    stackLayers,
    hasWeight,
    result,
    specificQtyEnabled,
    targetQty,
    additionalItems,
  } = props;
  const totalByUser = perFloor * stackLayers;
  const totalByWeight = hasWeight ? result.totalByWeight : Infinity;
  const rawEffectiveTotal = Math.min(totalByUser, totalByWeight);
  const isWeightLimited = totalByWeight < totalByUser;
  const parsedTargetQty = Math.max(Math.round(toNumber(targetQty)), 0);

  const effectiveTotal =
    specificQtyEnabled &&
    parsedTargetQty > 0 &&
    rawEffectiveTotal > parsedTargetQty
      ? parsedTargetQty
      : rawEffectiveTotal;

  const trucksNeeded =
    specificQtyEnabled && effectiveTotal > 0 && parsedTargetQty > 0
      ? Math.ceil(parsedTargetQty / effectiveTotal)
      : null;

  const activeAdditionalItems = activeItems(additionalItems);

  const additionalItemTotal = activeAdditionalItems.reduce(
    (sum, addedItem) => sum + addedItem.parsedQuantity,
    0,
  );
  const grandTotal =
    effectiveTotal === Infinity
      ? Infinity
      : effectiveTotal + additionalItemTotal;

  return {
    effectiveTotal,
    grandTotal,
    trucksNeeded,
    isWeightLimited,
    hasAdditionalItems: activeAdditionalItems.length > 0,
    activeAdditionalItems,
    parsedTargetQty,
  };
}
